import { css } from '@emotion/react'
import {
  Form,
  FormItem,
  FormElements,
  SelectInput,
  TextInput,
  Paragraph,
  colors,
  Button,
  DropInput,
  Avatar,
  Divider,
} from 'common-components'
import { TSociodemographicCriteriaValues } from '@definitions'
import { useSociodemographicResources } from '@hooks'
import { useAuth } from 'react-oidc-context'
import { UserType } from '@types'
import { useEffect } from 'react'
import { store } from '@store'

type UserPropsType = {
  user: UserType
}
const User = ({ user }: UserPropsType) => {
  const updateUserAction = store.use.updateUserAction().call
  const updateUrlAvatarUserAction = store.use.updateUrlAvatarUserAction().call
  const isLoadingUpdateUserAction = store.use.updateUserAction().status === 'LOADING'

  const [form] =
    FormElements.useForm<
      Pick<
        UserType,
        | 'idUser'
        | 'email'
        | 'phone'
        | 'lastName'
        | 'firstName'
        | 'nationality'
        | 'birthDate'
        | 'urlAvatar'
        | 'earning'
        | 'study'
        | 'family'
        | 'children'
        | 'job'
        | 'activitySector'
        | 'address'
      >
    >()
  const auth = useAuth()
  const { getResourceOptions } = useSociodemographicResources()

  useEffect(() => {
    if (user) {
      Object.keys(user).map((key) =>
        form.setFieldValue(
          key as Parameters<typeof form.setFieldValue>[0],
          user[key],
        ),
      )
    }
  }, [user])

  const onUpdateConfirm = async () => {
    updateUserAction({
      idUser: user.idUser,
      phone: form.getFieldValue('phone'),
      lastName: form.getFieldValue('lastName'),
      firstName: form.getFieldValue('firstName'),
      nationality: form.getFieldValue('nationality'),
      birthDate: form.getFieldValue('birthDate'),
      earning: form.getFieldValue('earning'),
      study: form.getFieldValue('study'),
      family: form.getFieldValue('family'),
      children: form.getFieldValue('children'),
      job: form.getFieldValue('job'),
      activitySector: form.getFieldValue('activitySector'),
      address: form.getFieldValue('address'),
    })
  }

  const onAvatarUpload = async (file: File) => {
    updateUrlAvatarUserAction({ idUser: user.idUser, file })
  }

  const getSelectInputPropsByName = (
    name: Parameters<typeof form.setFieldValue>[0],
    resourceType: TSociodemographicCriteriaValues,
  ) => {
    return {
      onChange: (_, option) => form.setFieldValue(name, option),
      value: form.getFieldValue(name)?.value?.value || null,
      options: getResourceOptions(resourceType),
    }
  }

  const fixDateValue = (value: string | Date | number | null | undefined) => {
    let d: Date | null = null

    if (!value) {
      d = null
    } else if (value instanceof Date) {
      d = isNaN(value.getTime()) ? null : value
    } else if (typeof value === 'number') {
      const tmp = new Date(value)
      d = isNaN(tmp.getTime()) ? null : tmp
    } else if (typeof value === 'string') {
      const tmp = new Date(value)
      d = isNaN(tmp.getTime()) ? null : tmp
    } else if (typeof (value as any)?.toDate === 'function') {
      try {
        const tmp = (value as any).toDate()
        d = isNaN(tmp.getTime()) ? null : tmp
      } catch {
        d = null
      }
    }

    // Résoud le problème d’affichage DD/MM/YYYY
    if (!d) {
      return null
    }
    const display: Date = new Date(d.toLocaleDateString('fr-FR'))
    return display.toLocaleDateString()
  }

  return (
    <div
      css={css`
        margin-top: 40px;
      `}
    >
      <div
        css={css`
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 1rem;
        `}
      >
        <div
          css={css`
            padding: 5px;
          `}
        >
          <Paragraph
            preset={'formTitle'}
            css={css`
              margin-bottom: 5px !important;
            `}
          >
            Général
          </Paragraph>
          <Paragraph
            css={css`
              margin-bottom: 0 !important;
            `}
          >
            This will be displayed on your profile.
          </Paragraph>
        </div>
        <Form form={form}>
          <FormItem name={'idUser'} label={'ID'}>
            <TextInput disabled />
          </FormItem>
          <div
            css={css`
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 1rem;
            `}
          >
            <FormItem name={'email'} label={'Email'}>
              <TextInput disabled />
            </FormItem>
            <FormItem name={'phone'} label={'Téléphone'}>
              <TextInput />
            </FormItem>
            <FormItem name={'firstName'} label={'Prénom'}>
              <TextInput />
            </FormItem>
            <FormItem name={'lastName'} label={'Nom'}>
              <TextInput />
            </FormItem>
            <FormItem
              name={'birthDate'}
              label={'Date de naissance'}
              getValueProps={(value) => ({
                value: fixDateValue(value),
              })}
            >
              <TextInput disabled />
            </FormItem>
            <FormItem name={'nationality'} label={'Nationalité'}>
              <SelectInput {...getSelectInputPropsByName('nationality', 'country')} />
            </FormItem>
          </div>
        </Form>
      </div>
      <Divider
        css={css`
          color: ${colors.gray200};
          width: 100%;
        `}
      />
      <div
        css={css`
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 1rem;
        `}
      >
        <div
          css={css`
            padding: 5px;
          `}
        >
          <Paragraph
            preset={'formTitle'}
            css={css`
              margin-bottom: 5px !important;
            `}
          >
            Votre avatar
          </Paragraph>
          <Paragraph
            css={css`
              margin-bottom: 0 !important;
            `}
          >
            Il sera affiché à coté de votre nom
          </Paragraph>
        </div>
        <div
          css={css`
            margin-bottom: 45px;
            display: flex;
            gap: 20px;
            justify-content: center;
            align-items: center;
          `}
        >
          <div
            css={css`
              gap: 15px;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
            `}
          >
            <Avatar size='2xl' src={user?.urlAvatar} />
          </div>
          <div
            css={css`
              width: 100%;
            `}
          >
            <DropInput
              beforeUpload={() => false}
              maxCount={1}
              onUpload={(ev) => onAvatarUpload(ev.fileList[0]?.originFileObj)}
            />
          </div>
        </div>
      </div>
      <Divider
        css={css`
          color: ${colors.gray200};
          width: 100%;
        `}
      />
      <div
        css={css`
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 1rem;
        `}
      >
        <div
          css={css`
            padding: 5px;
          `}
        >
          <Paragraph
            preset={'formTitle'}
            css={css`
              margin-bottom: 5px !important;
            `}
          >
            Socio démographique
          </Paragraph>
          <Paragraph
            css={css`
              margin-bottom: 0 !important;
            `}
          >
            This will be displayed on your profile.
          </Paragraph>
        </div>
        <Form form={form}>
          <div
            css={css`
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 1rem;
            `}
          >
            <FormItem name={'earning'} label={'Niveau de revenu'}>
              <SelectInput {...getSelectInputPropsByName('earning', 'earnings')} />
            </FormItem>
            <FormItem name={'study'} label={'Etudes'}>
              <SelectInput {...getSelectInputPropsByName('study', 'education')} />
            </FormItem>
            <FormItem name={'family'} label={'Situation familiale'}>
              <SelectInput {...getSelectInputPropsByName('family', 'maritalStatus')} />
            </FormItem>
            <FormItem name={'children'} label={'Nombre d’enfant'}>
              <SelectInput {...getSelectInputPropsByName('children', 'children')} />
            </FormItem>
            <FormItem name={'job'} label={'Profession'}>
              <SelectInput {...getSelectInputPropsByName('job', 'profession')} />
            </FormItem>
            <FormItem name={'activitySector'} label={'Secteur d’activité'}>
              <SelectInput {...getSelectInputPropsByName('activitySector', 'activity')} />
            </FormItem>
          </div>
        </Form>
      </div>
      <Divider
        css={css`
          color: ${colors.gray200};
          width: 100%;
        `}
      />
      <div
        css={css`
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 1rem;
        `}
      >
        <div
          css={css`
            padding: 5px;
          `}
        >
          <Paragraph
            preset={'formTitle'}
            css={css`
              margin-bottom: 5px !important;
            `}
          >
            Socio démographique
          </Paragraph>
          <Paragraph
            css={css`
              margin-bottom: 0 !important;
            `}
          >
            This will be displayed on your profile.
          </Paragraph>
        </div>
        <Form form={form}>
          <FormItem name={['address', 'path']} label={'Addresse'}>
            <TextInput />
          </FormItem>
          <div
            css={css`
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 1rem;
            `}
          >
            <FormItem name={['address', 'zipCode']} label={'Code Postal'}>
              <TextInput />
            </FormItem>
            <FormItem name={['address', 'city']} label={'Ville'}>
              <TextInput />
            </FormItem>
            <FormItem name={['address', 'region']} label={'Region'}>
              <SelectInput {...getSelectInputPropsByName(['address', 'region'], 'region')} />
            </FormItem>
            <FormItem name={['address', 'country']} label={'Pays'}>
              <SelectInput {...getSelectInputPropsByName(['address', 'country'], 'country')} />
            </FormItem>
          </div>
        </Form>
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: row-reverse;
        `}
      >
        <Button disabled={isLoadingUpdateUserAction} type='primary' onClick={onUpdateConfirm}>
          Enregistrer les modifications
        </Button>
      </div>
      <div
        css={css`
          border: 1px solid ${colors.gray200};
          padding: 24px;
          margin: 20px 0;
          border-radius: 12px;
        `}
      >
        <Paragraph preset='panelCard'>Se déconnecter</Paragraph>
        <Paragraph>
          Vous pouvez supprimer cette certification. Les résultats seront supprimés.
        </Paragraph>
        <Button
          css={css`
            border: 1px solid #d0d5dd;
            box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
            border-radius: 8px;
          `}
          type='outline'
          iconLeft='Trash01'
          onClick={async () => await auth.signoutRedirect()}
        >
          Se déconnecter
        </Button>
      </div>
    </div>
  )
}

export default User
